
import { supabase } from "@/integrations/supabase/client";
import { devLog, devError } from "@/utils/devLog";
import { Enrollment } from "@/types/enrollment";

/**
 * Fetches enrollments for the specified user
 * 
 * @param userId The ID of the user to fetch enrollments for
 * @returns Promise resolving to array of Enrollment objects
 */
export async function fetchEnrollments(userId?: string): Promise<Enrollment[]> {
  try {
    if (!userId) {
      devLog("[fetchEnrollments] No user ID provided");
      return [];
    }
    
    devLog("[fetchEnrollments] Fetching enrollments for user", { userId });
    
    // Start performance timer
    const startTime = performance.now();
    
    const { data, error, status } = await supabase.rpc('get_user_enrollments', { 
      p_user_id: userId 
    });
    
    // Calculate duration
    const duration = performance.now() - startTime;
      
    if (error) {
      devError("[fetchEnrollments] Error fetching enrollments:", error);
      devError(`[fetchEnrollments] Status code: ${status}`);
      return [];
    }
    
    if (!data || !Array.isArray(data)) {
      devError("[fetchEnrollments] No data returned or invalid format", {
        dataType: typeof data,
        isArray: Array.isArray(data)
      });
      return [];
    }
    
    // Type safety check for the first enrollment's course title
    const firstCourseTitle = data[0]?.courses && 
      typeof data[0].courses === 'object' && 
      data[0].courses !== null ? 
      (data[0].courses as any).title || 'N/A' : 
      'N/A';
    
    devLog("[fetchEnrollments] Retrieved enrollments", { 
      count: data.length || 0,
      duration: `${duration.toFixed(1)}ms`,
      firstCourseTitle
    });
    
    return data as Enrollment[];
  } catch (err) {
    devError("[fetchEnrollments] Unexpected error:", err);
    return [];
  }
}
